// Backend integration point — all API calls route through this module
// Base URL must be set via NEXT_PUBLIC_API_URL environment variable in production

// NOTE: Do NOT resolve API_BASE at module level — Next.js inlines NEXT_PUBLIC_* at build time,
// and a top-level const will be an empty string if the variable was unset during the build.
// Resolve inside fetchApi() so the value is always read fresh at call time.
const FALLBACK_API_BASE = 'https://cynthiaos-api-production.up.railway.app';

export type { FastRecord } from './fastCell';
export async function getFast():Promise<import('./fastCell').FastRecord[]> {
 const res=await fetch('/api/proxy?_path=/api/v1/fast',{cache:'no-store'});const json=await res.json();
 if(!res.ok)throw Error(json.error||'Fast could not be loaded.');return json.data;
}
export async function resetFast(revisions:import('./fastCell').FastRecord[]):Promise<{data:import('./fastCell').FastRecord[];source_report_date:string}> {
 const res=await fetch('/api/proxy?_path=/api/v1/fast/reset',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({revisions})});
 const json=await res.json();if(!res.ok)throw Error(json.error||'Reset failed. Your displayed values were kept.');return json;
}
export async function saveFast(category:string,furnishing:string,unit_numbers:string,revision:number):Promise<import('./fastCell').FastRecord>{
 const res=await fetch(`/api/proxy?_path=/api/v1/fast/${encodeURIComponent(category)}/${encodeURIComponent(furnishing)}`,{method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify({unit_numbers,revision})});
 const json=await res.json();if(!res.ok){const error=Object.assign(Error(json.error||'Save failed. Your input is kept.'),{current:json.current});throw error;}return json.data;
}

// ─── Canonical renewal window ─────────────────────────────────────────────────
// All user-facing renewal views and action queues use the same actionable
// population: non-family, non-employee leases due within the next 90 days.
export const QUERY_WINDOWS = {
  expiringSoonDays: 90,
  renewalFromDays: 0,
  renewalToDays: 90,
};

export interface LeaseExpiration {
  id: string;
  tenant_name: string;
  unit: string;
  unit_id?: string;
  property: string;
  lease_end_date: string | null;
  days_until_expiration: number;
  monthly_rent: number | null;
  contact_email: string | null;
  contact_phone: string | null;
  lease_type: string;
  unit_group: string | null;
}

export interface UpcomingRenewal {
  id: string;
  tenant_name: string;
  unit: string;
  property: string;
  renewal_date: string;
  days_until_renewal: number;
  current_rent: number;
  proposed_rent: number | null;
  renewal_status: 'pending' | 'in_progress' | 'signed' | 'declined';
  contact_email: string | null;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
  limit?: number;
  offset?: number;
}

export interface ApiError {
  message: string;
  status: number;
}

// ─── Response shape normalizer ────────────────────────────────────────────────
// Handles all known API response shapes and extracts the items array + total.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function extractArray(raw: any): { items: any[]; total: number } {
  // Shape 1: root array  →  [ ... ]
  if (Array.isArray(raw)) {
    return { items: raw, total: raw.length };
  }

  // Shape 2: { data: [...] }
  if (raw && Array.isArray(raw.data)) {
    return { items: raw.data, total: raw.total ?? raw.count ?? raw.data.length };
  }

  // Shape 3: { results: [...] }
  if (raw && Array.isArray(raw.results)) {
    return { items: raw.results, total: raw.total ?? raw.count ?? raw.results.length };
  }

  // Shape 4: { items: [...] }
  if (raw && Array.isArray(raw.items)) {
    return { items: raw.items, total: raw.total ?? raw.count ?? raw.items.length };
  }

  // Shape 5: { data: { items: [...] } }
  if (raw && raw.data && Array.isArray(raw.data.items)) {
    return { items: raw.data.items, total: raw.data.total ?? raw.total ?? raw.data.items.length };
  }

  // Shape 6: { data: { results: [...] } }
  if (raw && raw.data && Array.isArray(raw.data.results)) {
    return {
      items: raw.data.results,
      total: raw.data.total ?? raw.total ?? raw.data.results.length,
    };
  }

  // Fallback: unknown shape — log and return empty
  if (process.env.NODE_ENV === 'development')
    console.warn('[CynthiaOS API] Unknown response shape — could not extract array:', raw);
  return { items: [], total: 0 };
}

// ─── Field-name mapper for LeaseExpiration ────────────────────────────────────
// Maps any known API field variants to the canonical UI field names.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function formatTenantName(raw: any): string {
  // Prefer human-readable name fields over normalized IDs
  const name =
    raw.display_name ?? raw.full_name ?? raw.tenant_name ?? raw.tenantName ?? raw.tenant ?? '';
  const source = name || raw.tenant_id || '';
  if (!source) return '—';
  // Convert ALL_CAPS "LAST, FIRST" AppFolio format to "First Last"
  if (/^[A-Z][A-Z\s,.'\-]+$/.test(source) && source.includes(',')) {
    const [last, ...firstParts] = source.split(',').map((s: string) => s.trim());
    const first = firstParts.join(' ');
    return [first, last]
      .filter(Boolean)
      .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  }
  // Convert snake_case tenant_id slugs to Title Case (e.g. "dean_w_martin" → "Dean W Martin")
  if (/^[a-z_]+$/.test(source)) {
    return source
      .split('_')
      .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }
  return source;
}

// Compute a countdown at runtime from a date string (days from today,
// negative if past). Falls back to the provided stored value only when the
// date is missing or unparseable. Uses UTC date-only math to match the
// backend's CURRENT_DATE semantics.
function computeDaysUntil(dateStr: string | null, storedFallback: number): number {
  if (!dateStr) return storedFallback;
  const parsed = new Date(`${String(dateStr).slice(0, 10)}T00:00:00Z`);
  if (isNaN(parsed.getTime())) return storedFallback;
  const now = new Date();
  const todayUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.round((parsed.getTime() - todayUtc) / 86400000);
}

function mapLeaseExpiration(raw: any): LeaseExpiration {
  return {
    id: raw.id ?? raw.lease_id ?? raw._id ?? '',
    tenant_name: formatTenantName(raw),
    unit: raw.unit_id ?? raw.unit ?? raw.unit_number ?? raw.unitNumber ?? '',
    property: raw.property ?? raw.property_name ?? raw.propertyName ?? raw.building ?? '',
    lease_end_date:
      raw.lease_end_date ?? raw.leaseEndDate ?? raw.end_date ?? raw.expiration_date ?? null,
    // ALWAYS compute the countdown at runtime from the lease end date.
    // The stored days_until_expiration column in Gold has gone stale twice
    // in production (28 stale rows as of 2026-07-14, showing expired leases
    // with positive days remaining). The stored value is only a fallback
    // when no end date exists to compute from.
    days_until_expiration: computeDaysUntil(
      raw.lease_end_date ?? raw.leaseEndDate ?? raw.end_date ?? raw.expiration_date ?? null,
      raw.days_until_expiration ??
        raw.daysUntilExpiration ??
        raw.days_remaining ??
        raw.days_left ??
        0
    ),
    monthly_rent:
      raw.monthly_rent ??
      raw.monthlyRent ??
      raw.scheduled_rent ??
      raw.market_rent ??
      raw.rent ??
      raw.rent_amount ??
      null,
    contact_email: (raw.contact_email ?? raw.contactEmail ?? raw.email ?? '').trim() || null,
    contact_phone:
      (raw.contact_phone || raw.contactPhone || raw.phone || raw.phone_number || '').trim() || null,
    lease_type: raw.lease_type ?? raw.leaseType ?? raw.type ?? '',
    unit_group: raw.unit_group ?? null,
  };
}

// ─── Field-name mapper for UpcomingRenewal ────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapUpcomingRenewal(raw: any): UpcomingRenewal {
  return {
    id: raw.id ?? raw.renewal_id ?? raw._id ?? '',
    tenant_name: raw.tenant_name ?? raw.tenantName ?? raw.tenant ?? '',
    unit: raw.unit ?? raw.unit_number ?? raw.unitNumber ?? '',
    property: raw.property ?? raw.property_name ?? raw.propertyName ?? raw.building ?? '',
    renewal_date: raw.renewal_date ?? raw.renewalDate ?? raw.lease_end_date ?? raw.end_date ?? '',
    days_until_renewal: computeDaysUntil(
      raw.renewal_date ?? raw.renewalDate ?? raw.lease_end_date ?? raw.end_date ?? null,
      raw.days_until_renewal ?? raw.daysUntilRenewal ?? raw.days_remaining ?? raw.days_left ?? 0
    ),
    current_rent: raw.current_rent ?? raw.currentRent ?? raw.monthly_rent ?? raw.rent ?? 0,
    proposed_rent: raw.proposed_rent ?? raw.proposedRent ?? raw.new_rent ?? null,
    renewal_status: raw.renewal_status ?? raw.renewalStatus ?? raw.status,
    contact_email: raw.contact_email ?? raw.contactEmail ?? raw.email ?? '',
  };
}

// ─── Core fetch utility ───────────────────────────────────────────────────────
async function fetchApi<T>(endpoint: string, params?: Record<string, string | number>): Promise<T> {
  // Route through the Next.js proxy to avoid CORS — the proxy calls the real API server-side.
  const proxyUrl = new URL(
    '/api/proxy',
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000'
  );
  proxyUrl.searchParams.set('_path', endpoint);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      proxyUrl.searchParams.set(key, String(value));
    });
  }

  const fullUrl = proxyUrl.toString();
  const method = 'GET';

  try {
    const response = await fetch(fullUrl, {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
      cache: 'no-store',
    });

    if (!response.ok) {
      console.error('[CynthiaOS API] Non-OK response:', {
        fullUrl,
        method,
        status: response.status,
        statusText: response.statusText,
      });
      throw { message: response.statusText, status: response.status } as ApiError;
    }

    const json = await response.json();
    if (process.env.NODE_ENV === 'development')
      console.log(
        '[CynthiaOS API] Raw proxy response for',
        endpoint,
        '— length check:',
        Array.isArray(json)
          ? json.length
          : Array.isArray(json?.data)
            ? json.data.length
            : '(non-array root)',
        '| keys:',
        json && typeof json === 'object' ? Object.keys(json) : typeof json
      );
    return json as T;
  } catch (fetchError) {
    if (!(fetchError as ApiError).status) {
      console.error('[CynthiaOS API] fetch() failed:', {
        fullUrl,
        method,
        error: fetchError,
      });
    }
    throw fetchError;
  }
}

// ─── GET /api/v1/leases/expirations ──────────────────────────────────────────
// ─── Canonical active-lease population ───────────────────────────────────────
// THE single client-side definition of "active lease expirations": all
// future-dated leases (runtime-computed countdown), deduplicated to one row
// per unit keeping the soonest expiration, sorted soonest-first. Home, the
// Tasks page, and the Leases page must all consume this function — the July
// 2026 audit traced multiple cross-page count disagreements to each page
// re-implementing this dedup/filter independently. Do not fork this logic.
export async function getActiveLeasePopulation(): Promise<LeaseExpiration[]> {
  const page = await getLeasesExpiringSoon(1, 800);
  return page.data;
}

// Canonical population now comes from the server:
// /api/v1/leases/expirations?scope=active_future — the same v_lease_population
// view that feeds Jasmine and the pipeline reconciliation checks, so pages,
// agent, and Status can no longer drift. The response carries the actionable
// rows in data (family-held units excluded per the 2026-07-14 decision) and
// the family-held future leases separately in family_held for the Leases
// page's family block.
//
// The runtime-countdown mapper, the > 0 filter, and the per-unit dedup are
// kept as belt-and-suspenders: they are no-ops on a correct server response
// and have caught two stale-data incidents before.
export async function getActiveLeasePopulationWithFamily(): Promise<{
  active: LeaseExpiration[];
  familyHeld: LeaseExpiration[];
}> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = await fetchApi<any>('/api/v1/leases/expirations', {
    scope: 'renewals_due',
    days: QUERY_WINDOWS.renewalToDays,
    limit: 800,
  });

  const dedupSort = (rows: LeaseExpiration[]): LeaseExpiration[] => {
    const seenUnits = new Map<string, LeaseExpiration>();
    rows.forEach((r) => {
      const existing = seenUnits.get(r.unit);
      if (
        !existing ||
        (r.days_until_expiration ?? 9999) < (existing.days_until_expiration ?? 9999)
      ) {
        seenUnits.set(r.unit, r);
      }
    });
    return Array.from(seenUnits.values()).sort(
      (a, b) => (a.days_until_expiration ?? 9999) - (b.days_until_expiration ?? 9999)
    );
  };

  const { items } = extractArray(raw);
  const active = dedupSort(
    items.map(mapLeaseExpiration).filter((r) => r.days_until_expiration > 0)
  );
  // Family-held units are intentionally absent from the canonical renewal
  // scope, so no user-facing renewal page receives a separate family block.
  const familyHeld: LeaseExpiration[] = [];
  return { active, familyHeld };
}

export async function getLeaseExpirations(
  page = 1,
  perPage = 50
): Promise<PaginatedResponse<LeaseExpiration>> {
  const limit = perPage;
  const offset = (page - 1) * perPage;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = await fetchApi<any>('/api/v1/leases/expirations', {
    scope: 'renewals_due',
    days: QUERY_WINDOWS.renewalToDays,
    limit,
    offset,
  });

  const { items, total } = extractArray(raw);
  // Map first (which recomputes days_until_expiration from the lease end
  // date at runtime), THEN filter out expired leases. Filtering on the raw
  // stored column kept stale expired rows alive as positive-days records.
  const mapped = items.map(mapLeaseExpiration).filter((r) => r.days_until_expiration > 0);

  return {
    data: mapped,
    total: mapped.length,
    page,
    per_page: perPage,
    total_pages: Math.max(1, Math.ceil(mapped.length / perPage)),
    limit,
    offset,
  };
}

// ─── GET /api/v1/leases/expiring-soon ────────────────────────────────────────
export async function getLeasesExpiringSoon(
  page = 1,
  perPage = 50
): Promise<PaginatedResponse<LeaseExpiration>> {
  const limit = perPage;
  const offset = (page - 1) * perPage;

  // Canonical active_future scope windowed to expiringSoonDays — the same
  // server-side population as getActiveLeasePopulation, so "expiring soon"
  // is by construction a subset of the pages' lease universe. The legacy
  // /api/v1/leases/expiring-soon endpoint remains (rewired server-side to the
  // same view) for other consumers.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = await fetchApi<any>('/api/v1/leases/expirations', {
    scope: 'renewals_due',
    limit,
    offset,
    days: QUERY_WINDOWS.renewalToDays,
  });

  const { items, total } = extractArray(raw);
  // Map first (runtime countdown), then filter — see getLeaseExpirations.
  const mapped = items.map(mapLeaseExpiration).filter((r) => r.days_until_expiration > 0);

  return {
    data: mapped,
    total: mapped.length,
    page,
    per_page: perPage,
    total_pages: Math.max(1, Math.ceil(mapped.length / perPage)),
    limit,
    offset,
  };
}

// ─── GET /api/v1/leases/upcoming-renewals ────────────────────────────────────
export async function getUpcomingRenewals(
  page = 1,
  perPage = 50
): Promise<PaginatedResponse<UpcomingRenewal>> {
  const limit = perPage;
  const offset = (page - 1) * perPage;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = await fetchApi<any>('/api/v1/leases/upcoming-renewals', {
    limit,
    offset,
    from_days: QUERY_WINDOWS.renewalFromDays,
    to_days: QUERY_WINDOWS.renewalToDays,
  });

  const { items, total } = extractArray(raw);
  const mapped = items.map(mapUpcomingRenewal);

  return {
    data: mapped,
    total,
    page,
    per_page: perPage,
    total_pages: Math.max(1, Math.ceil(total / perPage)),
    limit,
    offset,
  };
}

// ─── Lease Actions API ────────────────────────────────────────────────────────

export interface LeaseActionsApiPayload {
  contacted: boolean;
  flagged: boolean;
  notes: string;
  last_action_at: string | null;
}

/** GET /api/v1/leases/actions/bulk — fetch all action records in one call. */
export async function getAllLeaseActionsFromApi(): Promise<Record<string, LeaseActionsApiPayload>> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const raw = await fetchApi<any>('/api/v1/leases/actions/bulk');
    const data = raw?.data ?? raw;
    if (!data || typeof data !== 'object') return {};
    return data as Record<string, LeaseActionsApiPayload>;
  } catch (err) {
    console.warn('[CynthiaOS API] GET /api/v1/leases/actions/bulk failed:', err);
    return {};
  }
}

/** GET /api/v1/leases/:id/actions — fetch persisted actions from backend. */
export async function getLeaseActionsFromApi(
  leaseId: string
): Promise<LeaseActionsApiPayload | null> {
  try {
    // API returns { success: true, data: { contacted, flagged, notes, last_action_at } }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const raw = await fetchApi<any>(`/api/v1/leases/${leaseId}/actions`);
    // Unwrap envelope if present
    const payload = raw?.data ?? raw;
    if (!payload || typeof payload !== 'object') return null;
    return payload as LeaseActionsApiPayload;
  } catch (err) {
    console.warn('[CynthiaOS API] GET /api/v1/leases/:id/actions failed:', err);
    return null;
  }
}

/** PUT /api/v1/leases/:id/actions — persist actions to backend. */
export async function putLeaseActionsToApi(
  leaseId: string,
  payload: LeaseActionsApiPayload
): Promise<LeaseActionsApiPayload | null> {
  const API_BASE = process.env.NEXT_PUBLIC_API_URL || FALLBACK_API_BASE;
  const url = `${API_BASE}/api/v1/leases/${leaseId}/actions`;
  try {
    const response = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      console.warn(
        '[CynthiaOS API] PUT /api/v1/leases/:id/actions non-OK:',
        response.status,
        response.statusText
      );
      return null;
    }
    const json = await response.json();
    // Unwrap envelope if present: { success: true, data: {...} }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const unwrapped = (json as any)?.data ?? json;
    return unwrapped as LeaseActionsApiPayload;
  } catch (err) {
    console.warn(
      '[CynthiaOS API] PUT /api/v1/leases/:id/actions failed — keeping local state:',
      err
    );
    return null;
  }
}

// ─── Unit Notes ─────────────────────────────────────────────────────────────

export interface UnitNotePayload {
  notes: string;
  contacted: boolean;
  flagged: boolean;
  updated_at: string | null;
  source: 'unit_notes' | 'lease_actions' | null;
}

/** GET /api/v1/units/:id/notes — fetch persisted note for a unit. */
export async function getUnitNotes(unitId: string): Promise<UnitNotePayload | null> {
  try {
    const raw = await fetchApi<UnitNotePayload>(
      `/api/v1/units/${encodeURIComponent(unitId)}/notes`
    );
    return raw ?? null;
  } catch (err) {
    console.warn('[CynthiaOS API] GET /api/v1/units/:id/notes failed:', err);
    return null;
  }
}

/** PUT /api/v1/units/:id/notes — persist notes, contacted, and/or flagged for a unit. */
export async function putUnitNotes(
  unitId: string,
  payload: { notes?: string; contacted?: boolean; flagged?: boolean }
): Promise<{
  unit_id: string;
  notes: string;
  contacted: boolean;
  flagged: boolean;
  updated_at: string;
} | null> {
  const API_BASE = process.env.NEXT_PUBLIC_API_URL || FALLBACK_API_BASE;
  const url = `${API_BASE}/api/v1/units/${encodeURIComponent(unitId)}/notes`;
  try {
    const response = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      console.warn(
        '[CynthiaOS API] PUT /api/v1/units/:id/notes non-OK:',
        response.status,
        response.statusText
      );
      return null;
    }
    const json = await response.json();
    return json.data ?? null;
  } catch (err) {
    console.warn('[CynthiaOS API] PUT /api/v1/units/:id/notes failed:', err);
    return null;
  }
}

// ─── Renewal Tracking ────────────────────────────────────────────────────────

export interface RenewalRecord {
  id: string;
  unit_id: string;
  tenant_id: string;
  tenant_name: string;
  lease_end_date: string;
  days_until_expiration: number;
  current_rent: number | null;
  proposed_rent: number | null;
  renewal_status: 'pending' | 'in_progress' | 'signed' | 'declined';
  contact_email: string | null;
  contact_phone: string | null;
  notes: string | null;
  tracking_updated_at: string | null;
}

export interface RenewalUpdatePayload {
  renewal_status?: 'pending' | 'in_progress' | 'signed' | 'declined';
  proposed_rent?: number | null;
  notes?: string | null;
}

/** GET /api/v1/renewals — fetch upcoming leases with renewal tracking data. */
export async function getRenewals(
  fromDays = QUERY_WINDOWS.renewalFromDays,
  toDays = QUERY_WINDOWS.renewalToDays,
  limit = 100,
  offset = 0
): Promise<{ data: RenewalRecord[]; total: number }> {
  try {
    const raw = await fetchApi<{ data: RenewalRecord[]; total: number }>('/api/v1/renewals', {
      from_days: fromDays,
      to_days: toDays,
      limit,
      offset,
    });
    return { data: raw?.data ?? [], total: raw?.total ?? 0 };
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[CynthiaOS API] GET /api/v1/renewals failed:', err);
    }
    return { data: [], total: 0 };
  }
}

/** PUT /api/v1/renewals/:unit_id — upsert renewal tracking for a unit. */
export async function updateRenewal(
  unitId: string,
  payload: RenewalUpdatePayload
): Promise<boolean> {
  const API_BASE =
    process.env.NEXT_PUBLIC_API_URL || 'https://cynthiaos-api-production.up.railway.app';
  const url = `${API_BASE}/api/v1/renewals/${encodeURIComponent(unitId)}`;
  try {
    const response = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return response.ok;
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[CynthiaOS API] PUT /api/v1/renewals/:unit_id failed:', err);
    }
    return false;
  }
}

// ─── Shared insight response wrapper ─────────────────────────────────────────
export interface InsightResponse<T> {
  success: boolean;
  total?: number;
  limit?: number;
  offset?: number;
  data: T[];
}

// ─── Portfolio Health ─────────────────────────────────────────────────────────
// ─── Management metric contract (R1 items 1.5-1.6) ───────────────────────────
export type MetricConfidence = 'trusted' | 'warning' | 'blocked';

export interface MetricContract {
  metric_id: string;
  label: string;
  value: number | null;
  population_definition: string;
  as_of: string;
  source_freshness: string | null;
  integrity_checked_at?: string | null;
  denominator?: number;
  denominator_definition?: string;
  confidence: MetricConfidence;
  affected_checks: string[];
  drilldown_url: string;
}

export interface MetricsSummary {
  occupancy_source?: { source_report_date: string | null; retrieved_at: string; freshness: string; warnings: string[] };
  success: boolean;
  as_of: string;
  metrics: MetricContract[];
}

// Metrics whose value is a fraction (0-1) and should render as a percentage.
export const RATE_METRIC_IDS = new Set(['occupancy_rate', 'vacancy_rate', 'profit_margin_ytd']);
// Metrics whose value is a dollar amount.
export const CURRENCY_METRIC_IDS = new Set([
  'collectible_exposure',
  'noi_ytd',
  'total_income_ytd',
]);

export async function getMetricsSummary(): Promise<MetricsSummary> {
  const res = await fetch('/api/proxy?_path=/api/v1/metrics/summary');
  const json = await res.json();
  if (!res.ok || json?.success === false) {
    throw new Error(json?.error ?? `API ${res.status}`);
  }
  return json;
}

export function metricsByIdFrom(summary: MetricsSummary | null): Map<string, MetricContract> {
  return new Map((summary?.metrics ?? []).map((m) => [m.metric_id, m]));
}

// ─── Release 2: Action layer + Today view ────────────────────────────────────
// ─── Release 4: Portfolio (executive) surface ────────────────────────────────
export interface PortfolioMetric {
  metric_id: string;
  label: string;
  value: number | null;
  target: number;
  direction: 'higher' | 'lower';
  unit: 'rate' | 'currency' | 'count';
  on_target: boolean;
  variance: number;
  variance_pct: number | null;
  target_is_default: boolean;
  status: 'on_target' | 'off_target';
}
export interface PortfolioExecView {
  success: boolean;
  as_of: string;
  on_target: number;
  off_target: number;
  metrics: PortfolioMetric[];
}
export async function getPortfolioExec(): Promise<PortfolioExecView> {
  const res = await fetch('/api/proxy?_path=/api/v2/portfolio');
  const json = await res.json();
  if (!res.ok || json?.success === false) throw new Error(json?.error ?? `API ${res.status}`);
  return json;
}

// ─── Release 3: Collections + Operations surfaces ────────────────────────────
export interface CollectionsView {
  success: boolean;
  as_of: string;
  headline: { label: string; value: number; tenants: number; note: string };
  stages: Record<string, { label: string; value: number; amount?: number }>;
}
export async function getCollections(): Promise<CollectionsView> {
  const res = await fetch('/api/proxy?_path=/api/v2/collections');
  const json = await res.json();
  if (!res.ok || json?.success === false) throw new Error(json?.error ?? `API ${res.status}`);
  return json;
}

export interface OperationsView {
  success: boolean;
  as_of: string;
  maintenance: { total: number; [k: string]: number | { label: string; value: number } };
  turns: Record<string, { label: string; value: number }>;
}
export async function getOperations(): Promise<OperationsView> {
  const res = await fetch('/api/proxy?_path=/api/v2/operations');
  const json = await res.json();
  if (!res.ok || json?.success === false) throw new Error(json?.error ?? `API ${res.status}`);
  return json;
}

// ─── Release 3: Leasing surface ──────────────────────────────────────────────
export interface LeasingView {
  success: boolean;
  as_of: string;
  lease_queues: Record<string, { label: string; value: number; urgent?: number; scope?: string }>;
  prospect_cohorts: {
    total: number;
    [k: string]: number | { label: string; value: number; note?: string; sub_auto_only?: number };
  };
}

export async function getLeasing(): Promise<LeasingView> {
  const res = await fetch('/api/proxy?_path=/api/v2/leasing');
  const json = await res.json();
  if (!res.ok || json?.success === false) throw new Error(json?.error ?? `API ${res.status}`);
  return json;
}

export interface ActionItem {
  action_id: string;
  natural_key: string | null;
  source: string;
  type: string;
  entity_type: string | null;
  entity_id: string | null;
  title: string;
  detail: string | null;
  owner: string;
  priority: 'high' | 'normal' | 'low';
  status: 'open' | 'in_progress' | 'snoozed' | 'done' | 'dismissed';
  due_at: string | null;
  impact_label: string | null;
  next_action: string | null;
  confidence: MetricConfidence;
}

export interface TodayOutcome {
  label: string;
  value: number;
  unit: 'currency' | 'count';
  sub: string;
  confidence: MetricConfidence;
  drilldown_url: string;
}

export interface TodayView {
  success: boolean;
  as_of: string;
  outcomes: Record<string, TodayOutcome>;
  queue: ActionItem[];
  /** Size of the returned (capped) queue array. */
  queue_total: number;
  /** True count of all open exceptions before the LIMIT cap. Optional so the
   *  frontend stays safe if the API hasn't deployed the field yet. */
  queue_total_open?: number;
}

export async function getToday(): Promise<TodayView> {
  const res = await fetch('/api/proxy?_path=/api/v2/today');
  const json = await res.json();
  if (!res.ok || json?.success === false) throw new Error(json?.error ?? `API ${res.status}`);
  return json;
}

export async function getActions(params?: {
  status?: string;
  type?: string;
  owner?: string;
}): Promise<{ total: number; data: ActionItem[] }> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  if (params?.type) qs.set('type', params.type);
  if (params?.owner) qs.set('owner', params.owner);
  const suffix = qs.toString() ? `&${qs.toString()}` : '';
  const res = await fetch(`/api/proxy?_path=/api/v2/actions${suffix}`);
  const json = await res.json();
  if (!res.ok || json?.success === false) throw new Error(json?.error ?? `API ${res.status}`);
  return { total: json.total, data: json.data ?? [] };
}

export async function transitionAction(
  actionId: string,
  patch: { status?: string; owner?: string; priority?: string; due_at?: string; snoozed_until?: string; note?: string }
): Promise<ActionItem> {
  const res = await fetch(`/api/proxy?_path=/api/v2/actions/${actionId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  const json = await res.json();
  if (!res.ok || json?.success === false) throw new Error(json?.error ?? `API ${res.status}`);
  return json.data;
}

export async function createAction(body: {
  title: string;
  detail?: string;
  type?: string;
  entity_type?: string;
  entity_id?: string;
  priority?: string;
  due_at?: string;
  next_action?: string;
}): Promise<ActionItem> {
  const res = await fetch('/api/proxy?_path=/api/v2/actions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await res.json();
  if (!res.ok || json?.success === false) throw new Error(json?.error ?? `API ${res.status}`);
  return json.data;
}

export type MetricConfidence2 = MetricConfidence;

export interface PortfolioHealthBreakdown {
  score: number;
  weight: string;
  description: string;
}

export interface PortfolioHealthSupportingMetrics {
  total_units: number | null;
  occupied_units: number | null;
  vacant_units: number | null;
  notice_units: number | null;
  occupancy_rate: number | null;
  vacancy_rate: number | null;
  net_operating_income: number;
  profit_margin: number | null;
  gross_revenue: number | null;
  total_delinquency_balance: number;
  avg_aged_receivables_risk_score: number;
  high_expiration_risk_count: number;
}

export interface PortfolioHealth {
  score_confidence?: 'trusted' | 'warning' | null;
  score_note?: string | null;
  success: boolean;
  portfolio_health_score: number;
  classification: string;
  breakdown: {
    financial: PortfolioHealthBreakdown;
    occupancy: PortfolioHealthBreakdown;
    risk: PortfolioHealthBreakdown;
  };
  supporting_metrics: PortfolioHealthSupportingMetrics;
  data_availability: {
    occupancy_data: boolean;
    financial_data: boolean;
    expense_data: boolean;
    risk_data: boolean;
  };
}

export async function getPortfolioHealth(): Promise<PortfolioHealth> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return fetchApi<any>('/api/v1/insights/portfolio-health');
}

// ─── At-Risk Revenue ──────────────────────────────────────────────────────────
export type UrgencyLevel = 'HIGH' | 'MEDIUM' | 'LOW';
export type AgingBucket = '0_30' | '31_60' | '61_90' | '90_plus';

export interface AtRiskTenant {
  tenant_id: string;
  full_name: string;
  unit_id: string;
  total_balance: number;
  risk_score: number;
  dominant_bucket: AgingBucket;
  delinquency_level: string | null;
  days_overdue: number | null;
  lease_end_date: string | null;
  days_until_expiration: number | null;
  urgency_level: UrgencyLevel;
}

export async function getAtRiskRevenue(
  urgency?: UrgencyLevel
): Promise<InsightResponse<AtRiskTenant>> {
  // Same 100-row backend cap as collections-risk — page through with offset
  // so the full at-risk total (dollar sum and tenant count) isn't silently
  // truncated. (The top-10 "At-Risk Revenue" widget was unaffected since it
  // only ever showed 10 rows, but the full list backing dashboard/insights
  // totals was capped at 100 of ~125 tenants.)
  const PAGE_SIZE = 100;
  let offset = 0;
  let declaredTotal: number | undefined;
  const all: AtRiskTenant[] = [];

  for (let page = 0; page < 20; page++) {
    const params: Record<string, string | number> = { limit: PAGE_SIZE, offset };
    if (urgency) params.urgency = urgency;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const raw = await fetchApi<any>('/api/v1/insights/at-risk-revenue', params);
    if (declaredTotal === undefined) declaredTotal = raw.total;
    const batch: AtRiskTenant[] = raw.data ?? [];
    all.push(...batch);
    if (batch.length < PAGE_SIZE) break;
    offset += PAGE_SIZE;
  }

  return { success: true, total: declaredTotal, limit: all.length, offset: 0, data: all };
}

// ─── Collections Risk ─────────────────────────────────────────────────────────
export type CollectionsClassification =
  | 'Immediate Action'
  | 'High Priority'
  | 'Monitor'
  | 'Low Risk';

export interface CollectionsRiskTenant {
  tenant_id: string;
  full_name: string;
  unit_id: string;
  tenant_status: 'current' | 'past';
  total_balance: number;
  risk_score: number;
  bucket_90_plus: number;
  dominant_bucket: AgingBucket;
  days_overdue: number | null;
  delinquency_level: string | null;
  lease_end_date: string | null;
  days_until_expiration: number | null;
  collections_risk_score: number;
  collections_classification: CollectionsClassification;
}

export async function getCollectionsRisk(
  classification?: CollectionsClassification
): Promise<InsightResponse<CollectionsRiskTenant>> {
  // The backend caps each response at 100 rows regardless of the requested
  // limit, so a single call silently truncates the collections list (this
  // previously showed only the first tenant / $300 of past-due balance on
  // Insights). Page through with offset until a short page signals the end.
  const PAGE_SIZE = 100;
  let offset = 0;
  let declaredTotal: number | undefined;
  const all: CollectionsRiskTenant[] = [];

  // Safety cap to avoid an infinite loop if the backend never returns a
  // short page (e.g. total is inconsistent with actual row count).
  for (let page = 0; page < 20; page++) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const params: Record<string, string | number> = { limit: PAGE_SIZE, offset };
    if (classification) params.classification = classification;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const raw = await fetchApi<any>('/api/v1/insights/collections-risk', params);
    if (declaredTotal === undefined) declaredTotal = raw.total;
    const batch: CollectionsRiskTenant[] = raw.data ?? [];
    all.push(...batch);
    if (batch.length < PAGE_SIZE) break;
    offset += PAGE_SIZE;
  }

  return { success: true, total: declaredTotal, limit: all.length, offset: 0, data: all };
}

// ─── Lease Expiration Risk ────────────────────────────────────────────────────
export type ExpirationRisk = 'HIGH' | 'MEDIUM' | 'LOW';

export interface LeaseExpirationRiskItem {
  tenant_id: string;
  full_name: string;
  unit_id: string;
  lease_end_date: string;
  days_until_expiration: number;
  risk_score: number | null;
  days_overdue: number | null;
  delinquency_level: string | null;
  expiration_risk: ExpirationRisk;
  /** true when lease_end_date has already passed and no renewal/move-in was detected */
  is_overdue: boolean;
}

export async function getLeaseExpirationRisk(
  risk?: ExpirationRisk,
  daysWindow?: number
): Promise<InsightResponse<LeaseExpirationRiskItem>> {
  const params: Record<string, string | number> = { limit: 200 };
  if (risk) params.risk = risk;
  if (daysWindow) params.days_window = daysWindow;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return fetchApi<any>('/api/v1/insights/lease-expiration-risk', params);
}

// ─── Turnover Velocity ────────────────────────────────────────────────────────
export type TurnoverClassification = 'High Churn' | 'Moderate' | 'Stable';

export interface TurnoverPortfolioSummary {
  total_turnover_events: number;
  units_with_turnover: number;
  total_units_tracked: number;
  avg_turnover_per_unit: number;
  stability_score: number;
  classification: string;
}

export interface TurnoverVelocityUnit {
  unit_id: string;
  number_of_move_ins: number;
  number_of_move_outs: number;
  turnover_count: number;
  first_event_date: string;
  last_event_date: string;
  stability_score: number;
  classification: TurnoverClassification;
}

export interface TurnoverVelocityResponse {
  success: boolean;
  total: number;
  limit: number;
  offset: number;
  portfolio: TurnoverPortfolioSummary;
  data: TurnoverVelocityUnit[];
}

export async function getTurnoverVelocity(): Promise<TurnoverVelocityResponse> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return fetchApi<any>('/api/v1/insights/turnover-velocity');
}

// ─── Delinquency ──────────────────────────────────────────────────────────────
export type DelinquencyRiskLevel = 'high' | 'medium' | 'low';

export interface DelinquencyRecord {
  id: string;
  bronze_report_id: string;
  tenant_id: string;
  unit_id: string;
  balance_due: number;
  days_overdue: number;
  risk_level: DelinquencyRiskLevel;
  created_at: string;
}

export async function getDelinquency(): Promise<InsightResponse<DelinquencyRecord>> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return fetchApi<any>('/api/v1/delinquency');
}

// ─── Aged Receivables ─────────────────────────────────────────────────────────
export interface AgedReceivableRecord {
  id: string;
  bronze_report_id: string;
  tenant_id: string;
  unit_id: string;
  total_balance: number;
  bucket_0_30: number;
  bucket_31_60: number;
  bucket_61_90: number;
  bucket_90_plus: number;
  dominant_bucket: AgingBucket;
  risk_score: number;
  created_at: string;
}

export async function getAgedReceivables(): Promise<InsightResponse<AgedReceivableRecord>> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return fetchApi<any>('/api/v1/aged-receivables');
}

// ─── Income ───────────────────────────────────────────────────────────────────
export interface IncomeStatement {
  id: string;
  bronze_report_id: string;
  report_date: string;
  // YTD fields
  total_income: number;
  rental_income: number;
  other_income: number;
  total_expenses: number;
  operating_expenses: number;
  net_operating_income: number;
  profit_margin: number | null;
  // MTD fields
  total_income_mtd: number;
  rental_income_mtd: number;
  other_income_mtd: number;
  total_expenses_mtd: number;
  operating_expenses_mtd: number;
  net_operating_income_mtd: number;
  created_at: string;
}

export async function getIncomeStatements(): Promise<InsightResponse<IncomeStatement>> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return fetchApi<any>('/api/v1/income');
}

// ─── Occupancy ────────────────────────────────────────────────────────────────
export interface OccupancySnapshot {
  id: string;
  bronze_report_id: string;
  report_date: string;
  total_units: number;
  occupied_units: number;
  vacant_units: number;
  occupancy_rate: number;
  vacancy_rate: number;
  created_at: string;
}

export async function getOccupancySnapshots(): Promise<InsightResponse<OccupancySnapshot>> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return fetchApi<any>('/api/v1/occupancy');
}

// ─── Turnover (raw Gold table) ────────────────────────────────────────────────
export type TurnoverEventType = 'move_in' | 'move_out';

export interface TurnoverEvent {
  id: string;
  bronze_report_id: string;
  tenant_id: string;
  unit_id: string;
  move_in_date: string | null;
  move_out_date: string | null;
  event_type: TurnoverEventType;
  created_at: string;
}

export async function getTurnoverEvents(): Promise<InsightResponse<TurnoverEvent>> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return fetchApi<any>('/api/v1/turnover');
}

/** Lightweight count-only fetch for expiring leases within N days */
export async function getExpiringCount(days: number): Promise<{ total: number }> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = await fetchApi<any>('/api/v1/leases/expiring-soon', { days, limit: 1 });
  return { total: raw?.total ?? 0 };
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── LEASING FUNNEL ───────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

export interface LeasingFunnelStage {
  stage: string;
  count: number;
  conversion_from_prev: number | null; // % that converted from prior stage
  drop_off_from_prev: number | null; // can be negative when leases > apps
  conversion_from_leads: number | null; // % relative to total leads
}

export interface LeasingFunnelPeriod {
  period: string; // "2026-01"
  period_label: string; // "Jan 2026"
  leads: number;
  applications: number;
  leases: number;
  lead_to_app_pct: number;
  app_to_lease_pct: number; // NOTE: can exceed 100 — lease_history includes
  // renewals/offline leases that skip formal apps
  lead_to_lease_pct: number;
}

export interface LeasingFunnelSummary {
  total_leads: number;
  total_applications: number;
  total_leases: number;
  lead_to_app_pct: number;
  app_to_lease_pct: number; // NOTE: can exceed 100 (see above)
  lead_to_lease_pct: number;
  period_from: string;
  period_to: string;
}

export interface LeasingFunnelResponse {
  success: boolean;
  summary: LeasingFunnelSummary;
  funnel: LeasingFunnelStage[];
  trend: LeasingFunnelPeriod[];
}

/**
 * GET /api/v1/insights/leasing-funnel
 * Derived from Bronze AppFolio reports: guest_cards, rental_applications,
 * lease_history. app_to_lease_pct can exceed 100% — lease_history includes
 * renewals and offline leases that bypass the formal application stage.
 */
export async function getLeasingFunnel(from?: string, to?: string): Promise<LeasingFunnelResponse> {
  const params: Record<string, string> = {};
  if (from) params.from = from;
  if (to) params.to = to;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return fetchApi<any>('/api/v1/insights/leasing-funnel', params);
}
